import React, { useEffect, useState, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Navbar, Nav } from 'react-bootstrap';

const BANNER_URL = 'https://iis.memphis.edu/wp-content/uploads/2025/03/cropped-UofM_horiz_rgb.jpg';
const REFRESH_INTERVAL = 5; // seconds

const UnapprovedPage = () => {
  const apiPrefix = window.location.pathname.match(/^\/[^\/]+/) ? window.location.pathname.match(/^\/[^\/]+/)[0].replace(/\/$/, '') : '';
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ip, setIp] = useState('');
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const intervalRef = useRef();

  // Get client IP
  useEffect(() => {
    const fetchIp = async () => {
      try {
        const res = await fetch('https://api.ipify.org?format=json');
        const data = await res.json();
        setIp(data.ip || '');
      } catch {
        setIp('');
      }
    };
    fetchIp();
  }, []);

  // Query backend for access request status for this IP
  const fetchStatus = async () => {
    if (!ip || ip.trim() === '') return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${apiPrefix}/api/access-requests?ip=${ip}`);
      const data = await res.json();
      if (res.ok && data && data.status) {
        setStatus(data.status);
      } else {
        setStatus(null);
        setError(data.error || 'No pending request found for your IP.');
      }
    } catch (err) {
      setError('Error fetching approval status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ip) return;
    fetchStatus();
    setCountdown(REFRESH_INTERVAL);
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          fetchStatus();
          return REFRESH_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line
  }, [ip]);

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      <header className="bg-white shadow-sm">
        <div className="container-fluid p-0">
          <img src={BANNER_URL} alt="Institute for Intelligent Systems Banner" className="img-fluid w-100" style={{ maxHeight: '90px', objectFit: 'contain', background: '#fff' }} />
        </div>
      </header>
      <div className="container py-4 flex-grow-1 d-flex flex-column align-items-center justify-content-center">
        <div className="card shadow-lg p-4 p-md-5" style={{ maxWidth: '500px', width: '100%' }}>
          <h2 className="card-title text-center mb-3">Overleaf Access Request Status</h2>
          <p className="card-subtitle text-muted text-center mb-4">Check the status of your access request for the Institute of Intelligent Systems at the University of Memphis.</p>
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <small>Refreshing in {countdown}s</small>
              <small className="text-muted" style={{ fontSize: '0.85em' }}>Auto-refresh</small>
            </div>
            <div className="progress" style={{ height: '8px' }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${((REFRESH_INTERVAL - countdown) / REFRESH_INTERVAL) * 100}%` }}
                aria-valuenow={REFRESH_INTERVAL - countdown}
                aria-valuemin="0"
                aria-valuemax={REFRESH_INTERVAL}
              />
            </div>
          </div>
          {loading ? (
            <div className="text-center">Checking your approval status...</div>
          ) : error ? (
            <div className="alert alert-danger text-center">{error}</div>
          ) : status === 'pending' ? (
            <div className="alert alert-warning text-center">
              <b>Your access request is still pending approval.</b><br />
              Please wait for an administrator to approve your request.
            </div>
          ) : status === 'approved' ? (
            <div className="alert alert-success text-center">
              <b>Your access request has been approved!</b><br />
              You should now be able to access Overleaf.
              <div className="d-flex justify-content-center mt-4">
                <a href="https://iis-overleaf.uom.memphis.edu/" className="btn btn-primary">Go to Overleaf</a>
              </div>
            </div>
          ) : (
            <div className="alert alert-info text-center">
              No access request found for your IP.<br />
              Please submit a new request if you need access.
              <div className="d-flex justify-content-center mt-4">
                <a href="../access-request" className="btn btn-primary">Request Access</a>
              </div>
            </div>
          )}
        </div>
      </div>
      <footer className="bg-white border-top shadow-sm mt-auto py-3">
        <div className="container text-center">
          <a href="../portal-admin" className="btn btn-outline-primary">Admin Portal</a>
        </div>
      </footer>
    </div>
  );
};

export default UnapprovedPage;
